import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Select from "react-select";
import toast, { Toaster } from "react-hot-toast";
import { get, post } from "../../services/ApiService";
import { Link } from "react-router-dom";

const ProductInventory = ({
  sqlProducts,
  variation,
  index,
  variations,
  setVariations,
  product,
  setCurrentVariationIndex,
  currentVariationIndex,
  vatCategories,
}) => {
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [isInventoryLoading, setIsInventoryLoading] = useState(false);
  const [showPipModal, setShowPipModal] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState(null);
  const [selectedPpid, setSelectedPpid] = useState(null);
  const [pipsList, setPipsList] = useState([]);
  const [isLoadingPips, setIsLoadingPips] = useState(false);
  const [fullPipsData, setFullPipsData] = useState({}); // Store full PIP data separately
  const [inventoryDetails, setInventoryDetails] = useState({
    products: [
      {
        id: "",
        quantity: "",
        pips: [], // Will store only pip.pip values
      },
    ],
    classification: "",
    vat_category: "",
    name: "",
    channel_ref: "",
  });

  const handleShowInventoryModal = () => {
    setCurrentVariationIndex(index);
    let name = product.title;
    if (
      Array.isArray(product.variations) &&
      variations.length > 1 &&
      variation.name
    ) {
      name = `${product.title} (${variation.name})`;
    }
    if (variation.inventory) {
      setInventoryDetails({
        products: variation.inventory.products?.length
          ? variation.inventory.products.map(p => ({
              ...p,
              pips: p.pips || [] // Already stores only pip.pip values
            }))
          : [{ id: "", quantity: "", pips: [] }],
        classification: variation.inventory.classification || "",
        vat_category: variation.inventory.vat_category || "",
        channel_ref: product.platform.toUpperCase(),
      });
    } else {
      setInventoryDetails({
        products: [{ id: "", quantity: "", pips: [] }],
        classification: "",
        vat_category: "",
        name: name,
        channel_ref: product.platform.toUpperCase(),
      });
    }
    setShowInventoryModal(true);
  };

  const handleCloseInventoryModal = () => {
    setShowInventoryModal(false);
  };

  const handleClosePipModal = () => {
    setShowPipModal(false);
    setSelectedProductIndex(null);
    setSelectedPpid(null);
    setPipsList([]);
  };

  const handleInventoryChange = (e) => {
    const { name, value } = e.target;
    setInventoryDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Function to handle PIP selection for a product
  const handleAddPipClick = (productIndex, ppid) => {
    setSelectedProductIndex(productIndex);
    setSelectedPpid(ppid);
    setShowPipModal(true);
    
    // Fetch PIPs for the selected product
    fetchPips(ppid);
  };

  // Fetch PIPs from API
  const fetchPips = async (ppid) => {
    if (!ppid) return;
    
    try {
      setIsLoadingPips(true);
      const response = await get(`api/products/${ppid}/pips`);
      
      if (response.success) {
        setPipsList(response.data || []);
        // Store full PIP data for display
        const pipDataMap = {};
        response.data.forEach(pip => {
          pipDataMap[pip.pip] = {
            pip: pip.pip,
            product: pip.product,
            id: pip.id
          };
        });
        setFullPipsData(pipDataMap);
      } else {
        toast.error("Failed to fetch PIPs");
        setPipsList([]);
        setFullPipsData({});
      }
    } catch (error) {
      console.error("Error fetching PIPs:", error);
      toast.error("Error fetching PIPs");
      setPipsList([]);
      setFullPipsData({});
    } finally {
      setIsLoadingPips(false);
    }
  };

  // Handle PIP selection - store only pip.pip in array
  const handlePipSelect = (selectedPip) => {
    if (selectedProductIndex !== null) {
      // Check if PIP already exists for this product
      const currentProduct = inventoryDetails.products[selectedProductIndex];
      const pipAlreadyExists = currentProduct.pips.some(pipValue => pipValue === selectedPip.pip);
      
      if (pipAlreadyExists) {
        toast.error("This PIP is already selected for this product");
        return;
      }
      
      // Update the pips array for the selected product - store only pip.pip
      setInventoryDetails((prev) => {
        const updatedProducts = [...prev.products];
        updatedProducts[selectedProductIndex] = {
          ...updatedProducts[selectedProductIndex],
          pips: [...updatedProducts[selectedProductIndex].pips, selectedPip.pip]
        };
        return {
          ...prev,
          products: updatedProducts,
        };
      });
    }
  };

  // Remove a PIP from product
  const handleRemovePip = (productIndex, pipValue) => {
    setInventoryDetails((prev) => {
      const updatedProducts = [...prev.products];
      updatedProducts[productIndex] = {
        ...updatedProducts[productIndex],
        pips: updatedProducts[productIndex].pips.filter(pip => pip !== pipValue)
      };
      return {
        ...prev,
        products: updatedProducts,
      };
    });
    
    toast.success("PIP removed successfully");
  };
  const applyVariationDefaultsToState = ({
    sku,
    inventory,
    defaultsResponse,
  }) => {
    const updatePayload = defaultsResponse?.updatePayload || {};

    setVariations((prevVariations) =>
      prevVariations.map((v) => {
        if (v.SKU !== sku) return v;

        return {
          ...v,

          // Keep newly saved inventory visible immediately
          inventory,

          // Add defaults returned by /variation/assign-defaults
          // Backend only returns fields it actually updated, so this should not
          // overwrite already-filled variation values.
          ...updatePayload,
        };
      })
    );
  };
const saveInventoryDetails = async () => {
  const { products, classification, vat_category, name, channel_ref } =
    inventoryDetails;

  const hasValidProduct = products.some(
    (product) => product.id && product.quantity
  );

  if (!hasValidProduct) {
    toast.error("At least one product must have both ID and quantity");
    return;
  }

  if (!classification || !vat_category) {
    toast.error("Classification and VAT category are required");
    return;
  }

  try {
    setIsInventoryLoading(true);

    const variation = variations[currentVariationIndex];

    const productsToSave = products
      .filter((p) => p.id && p.quantity)
      .map((product) => ({
        id: product.id,
        quantity: product.quantity,
        pips: product.pips || [],
      }));

    const inventoryPayload = {
      classification,
      vat_category,
      name,
      channel_ref,
      products: productsToSave,
    };

    const res = await post(`api/products/inventory/update`, {
      sku: variation.SKU,
      inventory: inventoryPayload,
    });

    if (!res.success) {
      toast.error(res.error || "Failed to update inventory");
      return;
    }

    /**
     * After inventory is saved, run the defaults assignment.
     * This updates Mongo variation fields such as:
     * weight, length, width, height, format, packagingType, postage, location, etc.
     */
    let defaultsResponse = null;

    try {
      defaultsResponse = await post(`api/variation/assign-defaults`, {
        sku: variation.SKU,
      });

      if (!defaultsResponse.success) {
        toast.error(
          defaultsResponse.error ||
            "Inventory saved, but failed to assign variation defaults"
        );
      }
    } catch (defaultsError) {
      console.error("Error assigning variation defaults:", defaultsError);
      toast.error("Inventory saved, but failed to assign variation defaults");
    }

    /**
     * Update frontend state immediately.
     * No page refresh needed.
     */
    applyVariationDefaultsToState({
      sku: variation.SKU,
      inventory: inventoryPayload,
      defaultsResponse,
    });

    toast.success("Inventory updated successfully");

    if (defaultsResponse?.updated) {
      toast.success("Variation defaults assigned");
    }

    handleCloseInventoryModal();
  } catch (error) {
    console.error("Error updating inventory:", error);
    toast.error("An error occurred while updating inventory");
  } finally {
    setIsInventoryLoading(false);
  }
};

  const handleProductChange = (index, field, value) => {
    setInventoryDetails((prev) => {
      const updatedProducts = [...prev.products];
      updatedProducts[index][field] = value;
      
      // Clear PIPs when product changes
      if (field === 'id') {
        updatedProducts[index].pips = [];
      }
      
      return {
        ...prev,
        products: updatedProducts,
      };
    });
  };

  const addProduct = () => {
    setInventoryDetails((prev) => ({
      ...prev,
      products: [...prev.products, { id: "", quantity: "", pips: [] }],
    }));
  };

  const removeProduct = (indexToRemove) => {
    if (inventoryDetails.products.length <= 1) {
      toast.error("At least one product is required");
      return;
    }

    setInventoryDetails((prev) => ({
      ...prev,
      products: prev.products.filter((_, index) => index !== indexToRemove),
    }));
  };

  // Helper function to get display text for PIP
  const getPipDisplayText = (pipValue) => {
    if (fullPipsData[pipValue]) {
      return `${pipValue} - ${fullPipsData[pipValue].product}`;
    }
    return pipValue; // Fallback if full data not available
  };

  return (
    <div>
      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        onClick={() => handleShowInventoryModal()}
      >
        <i className="fa fa-warehouse me-1"></i>
      </button>

      {/* Inventory Modal */}
      <Modal
        show={showInventoryModal}
        onHide={handleCloseInventoryModal}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Inventory Details - {variations[currentVariationIndex]?.SKU}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {inventoryDetails.products.map((productItem, index) => (
            <div
              key={index}
              className="mb-3"
              style={{
                border: "1px solid gray",
                padding: "10px",
                position: "relative",
              }}
            >
              <Link
                style={{ position: "absolute", top: "5px", right: "5px" }}
                onClick={() => removeProduct(index)}
                disabled={inventoryDetails.products.length <= 1}
              >
                <i className="fa fa-times"></i>
              </Link>

              <label className="form-label text-danger">
                Product {index + 1}
              </label>
              
              <Select
                className="mb-2"
                name="id"
                value={
                  sqlProducts.find((opt) => opt.value === productItem.id) ||
                  null
                }
                onChange={(selectedOption) =>
                  handleProductChange(index, "id", selectedOption?.value)
                }
                options={sqlProducts}
                isClearable
                placeholder="Select a product..."
              />

              <label className="form-label">Quantity</label>
              <input
                type="number"
                className="form-control mb-2"
                name="quantity"
                value={productItem.quantity}
                onChange={(e) =>
                  handleProductChange(index, "quantity", e.target.value)
                }
              />

              {/* PIP Button moved here under quantity */}
              <div className="mb-2">
                {productItem.id && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mt-4"
                    onClick={() => handleAddPipClick(index, productItem.id)}
                    title="Add PIP"
                  >
                    <i className="fa fa-link me-1"></i> Add PIP
                  </button>
                )}
              </div>

              {/* Display selected PIPs */}
              {productItem.pips && productItem.pips.length > 0 && (
                <div className="mb-2">
                  <small className="text-muted">Selected PIPs:</small>
                  <div className="mt-1">
                    {productItem.pips.map((pipValue, pipIndex) => (
                      <div 
                        key={pipIndex} 
                        className="d-flex align-items-center justify-content-between mb-1"
                      >
                        <div className="badge bg-dark">
                          {getPipDisplayText(pipValue)}
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger"
                            onClick={() => handleRemovePip(index, pipValue)}
                            title="Remove PIP"
                          >
                            <i className="fa fa-times fs-14"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="row align-items-center mb-3">
            <div className="col-auto">
              <Link onClick={addProduct}>
                <i className="fa fa-plus me-1"></i> Add Product
              </Link>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Classification</label>
            <select
              className="form-select"
              name="classification"
              value={inventoryDetails?.classification}
              onChange={handleInventoryChange}
            >
              <option value="">Select Classification</option>
              <option value="GSL">GSL</option>
              <option value="P">P</option>
              <option value="POM">POM</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">VAT Category</label>
            <select
              className="form-select"
              name="vat_category"
              value={inventoryDetails?.vat_category}
              onChange={handleInventoryChange}
            >
              <option value="">Select Vat Category</option>
              {vatCategories.map((category, index) => (
                <option
                  key={category.vatCategory}
                  value={category?.vatCategory}
                >
                  {category.vatCategory}
                </option>
              ))}
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseInventoryModal}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={saveInventoryDetails}
            disabled={isInventoryLoading}
          >
            {isInventoryLoading ? "Saving & assigning defaults..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* PIP Selection Modal */}
      <Modal
        show={showPipModal}
        onHide={handleClosePipModal}
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select PIPs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingPips ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading PIPs...</span>
              </div>
              <p className="mt-2">Loading PIPs...</p>
            </div>
          ) : pipsList.length > 0 ? (
            <div>
              <div className="list-group">
                {pipsList.map((pip, index) => {
                  // Check if PIP is already selected
                  const isSelected = selectedProductIndex !== null && 
                    inventoryDetails.products[selectedProductIndex]?.pips?.some(
                      selectedPip => selectedPip === pip.pip
                    );
                  
                  return (
                    <button
                      key={pip.id || index}
                      type="button"
                      className={`list-group-item list-group-item-action ${isSelected ? 'disabled' : ''}`}
                      onClick={() => !isSelected && handlePipSelect(pip)}
                      disabled={isSelected}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div><strong>{pip.pip}</strong></div>
                          <small className="text-muted">{pip.product}</small>
                        </div>
                        {isSelected && (
                          <span className="badge bg-success">
                            <i className="fa fa-check me-1"></i>
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-muted">No PIPs found for this product</p>
              <Button 
                variant="secondary" 
                onClick={handleClosePipModal}
              >
                Close
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleClosePipModal}
          >
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductInventory;